import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import PageTransition from '../components/PageTransition';
import BackToTop from '../components/BackToTop';
import { useMenu } from '../contexts/MenuContext';

export default function PrivacyPolicy() {
  const { isMenuButtonVisible, isSidebarOpen, isMobile } = useMenu();
  const navigate = useNavigate();
  
  // Scroll yönünü takip etmek için state ve ref
  const [showHomeButton, setShowHomeButton] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const lastScrollTop = useRef(0);

  // İlk yükleme tamamlandığında buton gösterimini aktifleştir
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoad(false);
    }, 500); // 500ms gecikme
    
    return () => clearTimeout(timer);
  }, []);

  // Scroll olayını dinle - butonu gizle/göster
  useEffect(() => {
    const handleScroll = () => {
      const st = window.pageYOffset || document.documentElement.scrollTop;
      const isModalOpen = document.body.hasAttribute('data-modal-open');
      
      // Modal açıksa butonu gizle
      if (isModalOpen) {
        setShowHomeButton(false);
        return;
      }
      
      // Modal kapandıysa, mevcut scroll pozisyonuna göre butonun durumunu yeniden değerlendir
      if (lastScrollTop.current === undefined || lastScrollTop.current === null) {
        setShowHomeButton(true);
        lastScrollTop.current = st;
        return;
      }
      
      // Buton için threshold (10px)
      if (st > lastScrollTop.current + 10) {
        setShowHomeButton(false);
      } else if (st < lastScrollTop.current - 10) {
        setShowHomeButton(true);
      }
      
      // Son scroll pozisyonunu kaydet
      lastScrollTop.current = st <= 0 ? 0 : st;
    };

    // Modal kapanma durumunu özel olarak kontrol eden fonksiyon
    const handleModalStateChange = () => {
      const isModalOpen = document.body.hasAttribute('data-modal-open');
      
      if (!isModalOpen) {
        // Modal kapandı - butonu tekrar göster
        const st = window.pageYOffset || document.documentElement.scrollTop;
        setShowHomeButton(true);
        lastScrollTop.current = st;
        
        // Kısa bir delay ile normal scroll davranışını başlat
        setTimeout(() => {
          handleScroll();
        }, 50);
      } else {
        // Modal açıldı - butonu gizle
        setShowHomeButton(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    // Body attribute değişikliklerini izle (modal açılıp kapanması için)
    const observer = new MutationObserver(handleModalStateChange);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-modal-open']
    });
    
    // İlk kontrol
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  return (
    <PageTransition>
      <div className="max-w-4xl mx-auto md:py-8 relative">

        {/* Ana Sayfaya Dönüş Butonu */}
        <AnimatePresence>
          {!isInitialLoad && showHomeButton && !(isMobile && isSidebarOpen) && (
            <motion.div
              className="fixed top-4 right-4 z-50"
              initial={{ scale: 0, opacity: 0, y: -20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0, opacity: 0, y: -20 }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 20,
                duration: 0.4
              }}
            >
              <motion.button
                onClick={() => navigate('/')}
                className="relative bg-gradient-to-r from-slate-500 to-slate-600 hover:from-slate-600 hover:to-slate-700 text-white p-2.5 rounded-full shadow-lg shadow-slate-500/25 hover:shadow-xl hover:shadow-slate-500/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500"
                aria-label="Ana Sayfaya Dön"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Content Section */}
        <div className="md:border md:border-gray-200 md:dark:border-gray-700 md:rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 md:shadow-lg">
          <div className="md:rounded-xl p-6 sm:p-8 md:border md:border-white md:dark:border-gray-700">
            
            {/* KVKK ve Gizlilik Politikası */}
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6 text-center">
                KVKK VE GİZLİLİK POLİTİKASI
              </h2>
              
              <div className="prose prose-gray dark:prose-invert max-w-none">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Bu gizlilik politikası, Google Play ve App Store mağaza girişlerindeki Bolat Servis &amp; Yedek Parça uygulaması için geçerlidir.
                </p>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  Google Play
                </h3>
                <ul className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6 list-disc pl-5 space-y-2">
                  <li>Uygulama adı: Bolat Servis &amp; Yedek Parça</li>
                  <li>Geliştirici adı: Olimpus Bilgisayar</li>
                  <li>Yasal tüzel kişi: OLİMPUS BİLGİSAYAR SİS. MÜH. SAN. VE TİC. LTD. ŞTİ.</li>
                </ul>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  App Store
                </h3>
                <ul className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6 list-disc pl-5 space-y-2">
                  <li>Uygulama adı: Bolat Servis &amp; Yedek Parça</li>
                  <li>Geliştirici adı: Olimpus Bilgisayar Sistemleri</li>
                  <li>Satıcı: OLİMPUS BİLGİSAYAR SİS. MÜH. SAN. VE TİC. LTD. ŞTİ.</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                  Bolat Servis &amp; Yedek Parça uygulaması, BEYBOLAT TARIM MAKİNALARI SAN. VE TİC. A.Ş. bayi servis ve yedek parça hizmetleri için sunulur. Google Play geliştirici adı Olimpus Bilgisayar, App Store geliştirici adı Olimpus Bilgisayar Sistemleri'dir.
                </p>

                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                  BEYBOLAT TARIM MAKİNALARI SAN. VE TİC. A.Ş. ("Şirketimiz" veya "Beybolat") olarak, web servis ve dijital kaynaklarımızın herhangi birini kullanan müşterilerimizin veya üçüncü kişilerin ("Kullanıcılar") kişisel verilerinin korunması, bu hususlarda şeffaflık ve karşılıklı güven oluşturmak önceliklerimizdendir. Web adresimiz{' '}
                  <a 
                    href="https://www.beybolat.com.tr" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline hover:no-underline transition-colors"
                  >
                    www.beybolat.com.tr
                  </a>
                  {' '}veya uzantıları ve bunlarla birlikte ama bunlarla sınırlı olmamak üzere sosyal medya sayfalarımızı ve/veya dijital kanallarımızı ziyaret etmeniz, hizmet ve ürünlerimizi kullandığınız veya destek hizmetlerimizden yararlandığınız durumlar gibi Beybolat'ı online ve dijital kanallarımız üzerinden ziyaret ettiğiniz anlarda otomatik olan veya olmayan yöntemlerle kaydettiğimiz ve elektronik olarak bizimle paylaştığınız kişisel verileriniz, öncelikle taleplerinizin yerine getirilmesini temin edebilmek, daha sonra ise size daha iyi hizmet sunmak amacıyla kullanılacaktır.
                </p>
                
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                  Onaylı veri tabanımızda yer almanız halinde ise, Şirketimiz tarafından sunulan ürün ve hizmetlerle ilgili olarak tanıtım, pazarlama ve promosyon faaliyetleri yapılması için ileti gönderilebilecektir. Şirketimiz tarafından gönderilen iletileri almak istemiyorsanız, size sunulan reddetme hakkını kullanarak örneğin; "Beybolat'tan yeni ürün ve hizmet tanıtımına ilişkin herhangi bir e-posta almak istemiyorsanız lütfen tıklayınız." bağlantısına tıklayarak, ilgili kanala ait listemizden çıkabilirsiniz.
                </p>
              </div>
            </div>

            {/* Sorumluluğun Sınırlandırılması */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Sorumluluğun Sınırlandırılması
              </h3>
              
              <div className="prose prose-gray dark:prose-invert max-w-none">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Bu internet sitesinde verilen bilgiler, "olduğu gibi", "mevcut olduğu şekilde" sağlanmaktadırlar. Beybolat, bu bilgilerin doğruluklarını, yeterliliklerini ve eksiksizliklerini garanti etmemekte ve bu bilgilerdeki hatalar ya da eksiklikler nedeniyle sorumluluğu, açık bir şekilde reddetmektedir. Bu bilgilerle üçüncü şahısların haklarının ihlal edilmemesi; mülkiyet, belirli bir amaç için uygunluk ve/veya bilgisayar virüsü bulunmamasına ilişkin garantiler dahil ancak bunlarla sınırlı kalmamak kaydıyla, zımnen, açıkça ya da yasal olarak hiçbir garanti vermemektedir.
                </p>
                
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Bu internet sitesinin kullanımı sırasında herhangi bir performans arızası, hata, eksiklik, kesinti kusur, işletme ve/veya nakilde gecikme, bilgisayar virüsü ve/veya hat ve/veya sistem arızası sonucu ortaya çıkan doğrudan ya da dolaylı zarar, ziyan ve masraflar da dahil ancak bunlarla sınırlı olmamak üzere hiçbir zarar ve ziyandan, Beybolat ve/veya çalışanları, bu tür bir zarar ve ziyan olasılığından haberdar edilmiş olsalar dahi, sorumlu olmazlar.
                </p>
                
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Gerek bu internet sitesinden bağlantı verilen sitelerdeki, gerekse de diğer internet sitelerine bağlantıların riski, kullanıcıya aittir. Bu sitelerde verilen bilgilerin doğruluğu ile bu sitelerden verilen diğer bağlantıların nitelikleri Beybolat tarafından araştırılmamış ve doğrulanmamıştır.
                </p>
                
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  Beybolat, bu internet sitesinde yer alan bütün ürün ve hizmetleri, sayfaları, bilgileri, görsel unsurları önceden bildirimde bulunmadan değiştirme hakkını saklı tutar.
                </p>
              </div>
            </div>

            {/* Footer Info */}
            <div className="border-t border-gray-200 dark:border-gray-600 pt-6 mt-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  © BEYBOLAT TARIM MAKİNALARI SAN. VE TİC. A.Ş.
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Son güncelleme: 8 Ekim 2026
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Back to Top Button */}
      <BackToTop />
    </PageTransition>
  );
}
